import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Building, Phone, ArrowRight, Briefcase, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('type') === 'freelancer' ? 'freelancer' : 'customer';
  
  const [activeTab, setActiveTab] = useState(initialTab); // 'customer' or 'freelancer'
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');

  // Check if already logged in
  useEffect(() => {
    const customerToken = localStorage.getItem('customer_token');
    const freelancerToken = localStorage.getItem('freelancer_token');
    
    if (customerToken) {
      navigate('/customer/dashboard');
    } else if (freelancerToken) {
      navigate('/freelancer/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (activeTab === 'customer') {
        if (isRegister) {
          // Customer Registration
          const res = await fetch(`${API_URL}/api/customer/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, password, phone, company })
          });

          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || 'Registrierung fehlgeschlagen');
          }

          const data = await res.json();
          localStorage.setItem('customer_token', data.token);
          localStorage.setItem('customer_user', JSON.stringify(data.customer));
          navigate('/customer/dashboard');
        } else {
          // Customer Login
          const res = await fetch(`${API_URL}/api/customer/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });

          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || 'Login fehlgeschlagen');
          }

          const data = await res.json();
          localStorage.setItem('customer_token', data.token);
          localStorage.setItem('customer_user', JSON.stringify(data.customer));
          navigate('/customer/dashboard');
        }
      } else {
        // Freelancer Login
        const res = await fetch(`${API_URL}/api/freelancer/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Login fehlgeschlagen');
        }

        const data = await res.json();
        localStorage.setItem('freelancer_token', data.token);
        localStorage.setItem('freelancer_user', JSON.stringify(data.freelancer));
        navigate('/freelancer/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setPhone('');
    setCompany('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/revocut-logo.png" alt="RevoCut" className="w-12 h-12" />
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              REVOCUT
            </span>
          </Link>
        </div>

        {/* Card */}
        <div 
          className="rounded-3xl border border-white/10 p-8"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
        >
          {/* Tabs */}
          <div className="flex gap-2 mb-8">
            <button
              onClick={() => { setActiveTab('customer'); resetForm(); setIsRegister(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'customer'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Users className="w-4 h-4" />
              Kunde
            </button>
            <button
              onClick={() => { setActiveTab('freelancer'); resetForm(); setIsRegister(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'freelancer'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Freelancer
            </button>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {isRegister ? 'Konto erstellen' : 'Willkommen zurück'}
            </h1>
            <p className="text-gray-400">
              {activeTab === 'customer' 
                ? (isRegister ? 'Erstelle dein Kundenkonto' : 'Melde dich an, um deine Projekte zu verwalten')
                : 'Melde dich in deinem Freelancer-Konto an'
              }
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (Register only) */}
            {isRegister && activeTab === 'customer' && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Dein Name"
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">E-Mail</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Passwort</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-12 pr-12 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Phone (Register only) */}
            {isRegister && activeTab === 'customer' && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Telefon (optional)</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+49 123 456789"
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                  />
                </div>
              </div>
            )}

            {/* Company (Register only) */}
            {isRegister && activeTab === 'customer' && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Firma (optional)</label>
                <div className="relative">
                  <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Deine Firma"
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-medium text-white transition-all hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#5C63FF' }}
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {isRegister ? 'Registrieren' : 'Einloggen'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Toggle Register/Login (Customer only) */}
          {activeTab === 'customer' && (
            <div className="mt-6 text-center">
              <p className="text-gray-400">
                {isRegister ? 'Bereits ein Konto?' : 'Noch kein Konto?'}
                <button
                  onClick={() => { setIsRegister(!isRegister); setError(''); }}
                  className="ml-2 text-purple-400 hover:text-purple-300 font-medium"
                >
                  {isRegister ? 'Einloggen' : 'Registrieren'}
                </button>
              </p>
            </div>
          )}

          {/* Forgot Password */}
          {!isRegister && (
            <div className="mt-4 text-center">
              <Link 
                to={activeTab === 'freelancer' ? '/freelancer/reset-password' : '/reset-password'}
                className="text-sm text-gray-500 hover:text-white transition-colors"
              >
                Passwort vergessen?
              </Link>
            </div>
          )}

          {/* Freelancer Notice with Apply Button */}
          {activeTab === 'freelancer' && (
            <div className="mt-6 space-y-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Hinweis:</strong> Als Freelancer wirst du von RevoCut eingeladen. 
                  Du erhältst deine Zugangsdaten per E-Mail.
                </p>
              </div>
              
              {/* Apply as Freelancer - Link to Team page with modal */}
              <div className="text-center">
                <p className="text-gray-400 mb-3">Du möchtest Teil unseres Netzwerks werden?</p>
                <Link 
                  to="/team?apply=true"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all bg-gradient-to-r from-purple-500 to-blue-500 hover:opacity-90"
                  data-testid="freelancer-apply-btn"
                >
                  <Briefcase className="w-4 h-4" />
                  Als Freelancer bewerben
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-500 hover:text-white transition-colors text-sm">
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
